"use client"

import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import { usePreferences } from "@/contexts/preferences-context"

const Toaster = ({ ...props }: ToasterProps) => {
  const { preferences } = usePreferences()
  const theme =
    preferences.theme === "system"
      ? (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
      : preferences.theme

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position="bottom-right"
      offset={16}
      mobileOffset={12}
      visibleToasts={6}
      closeButton
      toastOptions={{
        classNames: {
          toast:
            "group toast !relative !my-2 group-[.toaster]:rounded-xl group-[.toaster]:border group-[.toaster]:px-4 group-[.toaster]:py-3 group-[.toaster]:pl-14 group-[.toaster]:pr-10 group-[.toaster]:shadow-[0_12px_30px_-18px_rgba(15,23,42,0.55)] group-[.toaster]:backdrop-blur-md group-[.toaster]:overflow-hidden before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1",
          success:
            "!bg-emerald-50/95 !border-emerald-200 !text-emerald-900 dark:!bg-emerald-950/35 dark:!border-emerald-800/80 dark:!text-emerald-100 before:!bg-emerald-500",
          error:
            "!bg-rose-50/95 !border-rose-200 !text-rose-900 dark:!bg-rose-950/35 dark:!border-rose-800/80 dark:!text-rose-100 before:!bg-rose-500",
          warning:
            "!bg-amber-50/95 !border-amber-200 !text-amber-900 dark:!bg-amber-950/35 dark:!border-amber-800/80 dark:!text-amber-100 before:!bg-amber-500",
          info:
            "!bg-blue-50/95 !border-blue-200 !text-blue-900 dark:!bg-blue-950/35 dark:!border-blue-800/80 dark:!text-blue-100 before:!bg-blue-500",
          loading:
            "!bg-slate-50/95 !border-slate-200 !text-slate-900 dark:!bg-slate-900/65 dark:!border-slate-700 dark:!text-slate-100 before:!bg-slate-500",
          title: "text-[0.92rem] font-semibold leading-4 tracking-tight",
          description: "mt-0.5 text-[13px] !text-current/75 leading-5",
          icon: "!relative !left-0 !top-0 !translate-y-0 !mr-3 shrink-0",
          actionButton:
            "!bg-primary !text-primary-foreground hover:!bg-primary/90 !rounded-md !px-3 !h-8 !text-xs !font-semibold",
          cancelButton:
            "!bg-muted !text-foreground hover:!bg-muted/80 !rounded-md !px-3 !h-8 !text-xs !font-medium",
          closeButton:
            "!absolute !left-auto !top-1/2 !-translate-y-1/2 !right-2 !z-30 !m-0 !h-6 !w-6 !p-0 !rounded-md !bg-white/60 hover:!bg-white dark:!bg-black/30 dark:hover:!bg-black/45 !border !border-current/15 !text-current/70 hover:!text-current !shadow-none",
        },
      }}
      icons={{
        success: (
          <span className="inline-flex items-center justify-center rounded-full p-1 bg-emerald-100 text-emerald-600 dark:bg-emerald-900/55 dark:text-emerald-300">
            <CircleCheckIcon className="size-4" />
          </span>
        ),
        info: (
          <span className="inline-flex items-center justify-center rounded-full p-1 bg-blue-100 text-blue-600 dark:bg-blue-900/55 dark:text-blue-300">
            <InfoIcon className="size-4" />
          </span>
        ),
        warning: (
          <span className="inline-flex items-center justify-center rounded-full p-1 bg-amber-100 text-amber-600 dark:bg-amber-900/55 dark:text-amber-300">
            <TriangleAlertIcon className="size-4" />
          </span>
        ),
        error: (
          <span className="inline-flex items-center justify-center rounded-full p-1 bg-rose-100 text-rose-600 dark:bg-rose-900/55 dark:text-rose-300">
            <OctagonXIcon className="size-4" />
          </span>
        ),
        loading: (
          <span className="inline-flex items-center justify-center rounded-full p-1 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <Loader2Icon className="size-4 animate-spin" />
          </span>
        ),
      }}
      style={{ "--border-radius": "12px" } as React.CSSProperties}
      {...props}
    />
  )
}

export { Toaster }
